import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Popover,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';

import { componentsSize } from 'constants/components';
import { useProjectFilter } from 'hooks/useProjectFilter';
import { SearchInput, Status } from 'components/simple';
import { CrossIcon as CloseIcon, FilterIcon } from 'assets/icons';

const StyledCell = styled(TableCell)(({ theme: { spacing } }) => ({
  border: 'none',
  ':last-of-type': {
    paddingRight: spacing(2),
  },
}));

const styles = {
  paper: {
    borderRadius: '38px',
    width: 710,
    height: 685,
    paddingInline: 8,
    paddingTop: 10,
    paddingBottom: 5,
    margin: 0,
    position: 'absolute',
  },
  closeButton: ({ palette }) => ({
    position: 'absolute',
    top: 40,
    right: 48,
    color: palette.colors.darkGrey,
  }),
  icon: ({ palette }) => ({
    width: 16,
    height: 16,
    color: palette.text.primary,
  }),
  tableContainer: ({ palette }) => ({
    height: 290,
    marginBottom: 4,
    paddingBlock: 1,
    borderRadius: '6px',
    backgroundColor: palette.colors.lightPale,
    border: `1px solid ${palette.colors.lightPale}`,
  }),
  tableRow: {
    cursor: 'pointer',
    backgroundColor: 'transparent !important',
  },
  rowCheckbox: ({ palette }) => ({
    color: `${palette.common.black} !important`,
  }),
  nameCell: {
    maxWidth: 0,
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: 1,
  },
  searchInput: ({ palette }) => ({
    borderRadius: '6px',
    marginBottom: 3,
    backgroundColor: palette.colors.lightPale,
    width: '100% !important',
    '.MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${palette.colors.lightPale} !important`,
    },
  }),
  filterButton: { minWidth: 110 },
};

const ProjectFilterPopover = ({ projectsData, selectedProjects, setSelectedProjects }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { projectIds, resetProjectIds, projectList, tableBarButtons, searchValue, setSearchValue, onSelectProject } =
    useProjectFilter(projectsData, selectedProjects);

  const handleOpen = (event) => {
    resetProjectIds();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSubmit = () => {
    setSelectedProjects(projectIds);
    handleClose();
  };

  return (
    <>
      <Button
        size="small"
        color="info"
        onClick={handleOpen}
        startIcon={<FilterIcon color="white" />}
        id="filter-button"
        aria-haspopup="true"
        aria-controls={open ? 'filter-content' : undefined}
        aria-expanded={open ? 'true' : undefined}
        sx={styles.filterButton}
      >
        Filter
      </Button>

      <Popover
        disableAutoFocus
        id="filter-content"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        aria-labelledby="filter-button"
        slotProps={{
          paper: { sx: styles.paper },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <IconButton onClick={handleClose} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>

        <Typography variant="h2-bold" paragraph>
          Add or Remove Projects
        </Typography>

        <SearchInput setSearchValue={setSearchValue} searchValue={searchValue} customStyles={styles.searchInput} />

        <Typography variant="button" component="p" align="left" marginBottom={2}>
          Project List
        </Typography>

        <Stack direction="row" columnGap={2} alignItems="center" marginBottom={1}>
          <Typography variant="body2">Select:</Typography>

          {tableBarButtons.map(({ label, Icon, onClick }) => (
            <Button
              key={label}
              variant="text"
              size="small"
              onClick={onClick}
              startIcon={<Box component={Icon} sx={styles.icon} />}
            >
              <Typography variant="body2" component="span">
                {label}
              </Typography>
            </Button>
          ))}
        </Stack>

        <TableContainer sx={styles.tableContainer}>
          <Table>
            <TableBody>
              {projectList.map(({ id, name, status }) => {
                const isSelectedRow = projectIds.includes(id);
                const labelId = `projects-table-checkbox-${id}`;

                return (
                  <TableRow
                    hover
                    key={id}
                    role="checkbox"
                    onClick={() => onSelectProject(id)}
                    aria-checked={isSelectedRow}
                    selected={isSelectedRow}
                    sx={styles.tableRow}
                  >
                    <StyledCell padding="checkbox">
                      <Checkbox
                        checked={isSelectedRow}
                        inputProps={{ 'aria-labelledby': labelId }}
                        sx={styles.rowCheckbox}
                      />
                    </StyledCell>

                    <StyledCell scope="row" id={labelId} padding="none" sx={styles.nameCell}>
                      {name}
                    </StyledCell>

                    <StyledCell padding="none">
                      <Status size={componentsSize.small} statusType={status} />
                    </StyledCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction="row" justifyContent="end">
          <Button size="small" color="info" onClick={handleSubmit}>
            Done
          </Button>
        </Stack>
      </Popover>
    </>
  );
};

ProjectFilterPopover.propTypes = {
  projectsData: PropTypes.arrayOf(
    PropTypes.shape({
      projectName: PropTypes.string,
      id: PropTypes.string,
      status: PropTypes.string,
    }),
  ).isRequired,
  selectedProjects: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedProjects: PropTypes.func.isRequired,
};

export default ProjectFilterPopover;
